import { Alert, Box, Button, HStack, Text, VStack } from '@chakra-ui/react'
import { ArrowLeft } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { NavigateFunction } from 'react-router'
import { Link, useBlocker } from 'react-router'

import type { ToastLike } from '@mangovoice/mango-ui'

import { DevicesApiProvider } from '../api/context'
import type { DevicesApi } from '../api/devicesApi'
import {
  DeviceButtonGrid,
  DeviceForm,
  type DeviceFormHandle,
} from '../components'
import { useGetDevice, useGetDeviceModels, useUpdateDevice } from '../hooks'
import { DevicesI18nProvider, useDevicesI18n } from '../i18n'
import type { DevicesTranslationLike } from '../i18n'
import type { DeviceButtonPayload } from '../types'
import {
  hasInvalidDeviceButtonConfiguration,
  resolveExtensionId,
  sanitizeDeviceButtonsForApi,
  TOAST_DURATION_SUCCESS,
} from '../utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DeviceEditPageProps {
  api: DevicesApi
  pbxId: number
  deviceId: number
  navigate: NavigateFunction
  toast: ToastLike
  listRoute: string
  wrapper?: React.ComponentType<{ children: React.ReactNode }>
  UnsavedChangesDialog?: React.ComponentType<{
    blocker: ReturnType<typeof useBlocker>
  }>
  /** Optional translation function for i18n; when omitted, components use fallback English */
  t?: DevicesTranslationLike
  /** Mirrors legacy behavior for internal-only fields (Warranty/Source). */
  showInternalFields?: boolean
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const DeviceEditPage = (props: DeviceEditPageProps) => {
  return (
    <DevicesApiProvider api={props.api}>
      <DevicesI18nProvider t={props.t}>
        <DeviceEditPageInner {...props} />
      </DevicesI18nProvider>
    </DevicesApiProvider>
  )
}

const DeviceEditPageInner = ({
  pbxId,
  deviceId,
  navigate,
  toast,
  listRoute,
  wrapper: Wrapper,
  UnsavedChangesDialog,
  showInternalFields = true,
}: DeviceEditPageProps) => {
  const { text } = useDevicesI18n()
  const { data: device, isLoading: isLoadingDevice } = useGetDevice(
    pbxId,
    deviceId,
  )
  const { data: models = [] } = useGetDeviceModels()
  const { mutate: updateDeviceMutate, isPending: isSubmitting } =
    useUpdateDevice(pbxId)

  const formRef = useRef<DeviceFormHandle>(null)
  const [canSubmit, setCanSubmit] = useState(false)
  const [formDirty, setFormDirty] = useState(false)
  const [formValidationMessages, setFormValidationMessages] = useState<
    string[]
  >([])

  const [selectedModelId, setSelectedModelId] = useState<number | null>(null)
  const [sidecarEnabled, setSidecarEnabled] = useState(false)
  const [primaryExtensionId, setPrimaryExtensionId] = useState<number | null>(
    null,
  )
  const [lineButtonCount, setLineButtonCount] = useState<number | null>(null)
  const [hasButtonValidationError, setHasButtonValidationError] =
    useState(false)

  const handleModelChange = useCallback((modelId: number | null) => {
    setSelectedModelId(modelId)
  }, [])

  const handleSidecarChange = useCallback((enabled: boolean) => {
    setSidecarEnabled(enabled)
  }, [])

  const model = selectedModelId
    ? (models.find((m) => m.id === selectedModelId) ?? null)
    : device
      ? (models.find((m) => m.id === device.device_model) ?? null)
      : null

  const [buttons, setButtons] = useState<DeviceButtonPayload[]>([])
  const [hasButtonEdits, setHasButtonEdits] = useState(false)
  const blocker = useBlocker(formDirty || hasButtonEdits)

  useEffect(() => {
    if (device?.buttons) {
      setButtons(
        device.buttons.map((b) => ({
          type: b.type,
          display_count: b.display_count,
          button_function: b.button_function,
          button_value: b.button_value,
          button_label: b.button_label,
          extension: resolveExtensionId(b.extension),
          speed_dial_value: b.speed_dial_value,
          slot_number: b.slot_number,
        })),
      )
    }
    setHasButtonEdits(false)
  }, [device])

  const handleButtonsChange = useCallback(
    (newButtons: DeviceButtonPayload[]) => {
      setHasButtonEdits(true)
      setButtons(newButtons)
    },
    [],
  )

  const hasInvalidButtonConfiguration = useMemo(
    () =>
      hasInvalidDeviceButtonConfiguration(buttons, {
        includeSidecarButtons: sidecarEnabled,
        maxLineButtons: lineButtonCount ?? undefined,
      }),
    [buttons, lineButtonCount, sidecarEnabled],
  )

  const hasButtonValidationProblem =
    hasButtonValidationError || hasInvalidButtonConfiguration

  const saveBlockingMessages = useMemo(() => {
    const messages = new Set(formValidationMessages)
    if (hasButtonValidationProblem) {
      messages.add(
        text(
          'devices.form.buttonConfigurationInvalid',
          'Fix the invalid button configuration before saving.',
        ),
      )
    }
    return [...messages]
  }, [formValidationMessages, hasButtonValidationProblem, text])

  const submitStateRef = useRef({
    buttons,
    hasButtonValidationProblem,
    sidecarEnabled,
    lineButtonCount,
    hasButtonEdits,
    device,
  })
  useLayoutEffect(() => {
    submitStateRef.current = {
      buttons,
      hasButtonValidationProblem,
      sidecarEnabled,
      lineButtonCount,
      hasButtonEdits,
      device,
    }
  })

  const handleSubmit = useCallback(
    (
      payload: Parameters<typeof updateDeviceMutate>[0]['payload'],
      reboot?: boolean,
    ) => {
      const {
        buttons: currentButtons,
        hasButtonValidationProblem: hasValidationProblem,
        sidecarEnabled: currentSidecarEnabled,
        lineButtonCount: currentLineButtonCount,
        hasButtonEdits: currentHasButtonEdits,
        device: currentDevice,
      } = submitStateRef.current

      if (hasValidationProblem) {
        setHasButtonValidationError(true)
        return
      }

      const submitButtons = sanitizeDeviceButtonsForApi(currentButtons, {
        primaryExtensionId:
          payload.primary_extension_id ?? currentDevice?.primary_extension?.id,
        includePrimaryLineFallback: true,
        includeSidecarButtons: currentSidecarEnabled,
        maxLineButtons: currentLineButtonCount ?? undefined,
      })

      const shouldSendButtons =
        currentHasButtonEdits || currentDevice?.buttons != null
      const payloadWithButtons = shouldSendButtons
        ? { ...payload, buttons: submitButtons }
        : payload

      updateDeviceMutate(
        { deviceId, payload: payloadWithButtons, reboot },
        {
          onSuccess: (result) => {
            setFormDirty(false)
            setHasButtonEdits(false)

            if (result?.warning) {
              toast.create({
                title: text(
                  'devices.toast.deviceUpdatedWithWarning',
                  'Device updated with warning',
                ),
                description: result.warning,
                type: 'warning',
              })
            } else {
              toast.create({
                title: reboot
                  ? text(
                      'devices.toast.deviceUpdatedRebooting',
                      'Device updated and rebooting',
                    )
                  : text('devices.toast.deviceUpdated', 'Device updated'),
                type: 'success',
                duration: TOAST_DURATION_SUCCESS,
              })
            }
          },
          onError: (error) => {
            toast.create({
              title: text(
                'devices.toast.updateFailed',
                'Failed to update device',
              ),
              description: error?.message ?? 'Unknown error',
              type: 'error',
            })
          },
        },
      )
    },
    [deviceId, updateDeviceMutate, text, toast],
  )

  const handleCancel = useCallback(() => {
    navigate(listRoute)
  }, [navigate, listRoute])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return
    const target = e.target as HTMLElement
    if (target.tagName === 'TEXTAREA') return
    e.preventDefault()
    formRef.current?.submit(false)
  }, [])

  if (!pbxId) {
    return null
  }

  const content = (
    <Box flex={1} overflow="auto" p={8} onKeyDown={handleKeyDown}>
      <VStack align="stretch" gap={6}>
        <HStack>
          <Button
            asChild
            variant="ghost"
            size="sm"
            colorPalette="neutral"
            alignSelf="flex-start"
          >
            <Link to={listRoute}>
              <ArrowLeft />
              {text('devices.common.backToList', 'Back to list')}
            </Link>
          </Button>
        </HStack>
        <DeviceForm
          ref={formRef}
          mode="edit"
          pbxId={pbxId}
          device={device ?? null}
          isLoadingDevice={isLoadingDevice}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
          onModelChange={handleModelChange}
          onSidecarChange={handleSidecarChange}
          onPrimaryExtensionChange={setPrimaryExtensionId}
          hideActions
          onCanSubmitChange={setCanSubmit}
          onDirtyChange={setFormDirty}
          onValidationMessagesChange={setFormValidationMessages}
          onLineButtonCountChange={setLineButtonCount}
          showInternalFields={showInternalFields}
        />
        {device && (
          <DeviceButtonGrid
            pbxId={pbxId}
            sidecarEnabled={sidecarEnabled}
            primaryExtensionId={primaryExtensionId}
            model={model}
            buttons={buttons}
            onChange={handleButtonsChange}
            lineButtonCount={lineButtonCount ?? undefined}
            onButtonValidationError={setHasButtonValidationError}
          />
        )}
        {device && (
          <Box
            p={6}
            borderTopWidth={1}
            borderColor="border.default"
            bg="bg.panel"
            borderRadius="lg"
          >
            {saveBlockingMessages.length > 0 && (
              <Alert.Root status="error" mb={4}>
                <Alert.Indicator />
                <Box>
                  <Text fontWeight="medium" fontSize="sm">
                    {text('devices.form.cannotSaveTitle', 'Cannot save yet')}
                  </Text>
                  {saveBlockingMessages.map((message) => (
                    <Text key={message} fontSize="sm">
                      {message}
                    </Text>
                  ))}
                </Box>
              </Alert.Root>
            )}
            <HStack gap={3} justifyContent="flex-end">
              <Button
                type="button"
                variant="outline"
                colorPalette="neutral"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                {text('devices.common.cancel', 'Cancel')}
              </Button>
              <Button
                type="button"
                colorPalette="brand"
                onClick={() => formRef.current?.submit(false)}
                loading={isSubmitting}
                disabled={!canSubmit || hasButtonValidationProblem}
              >
                {text('devices.common.save', 'Save')}
              </Button>
              <Button
                type="button"
                colorPalette="brand"
                variant="outline"
                onClick={() => formRef.current?.submit(true)}
                loading={isSubmitting}
                disabled={!canSubmit || hasButtonValidationProblem}
              >
                {text('devices.common.saveAndReboot', 'Save & Reboot')}
              </Button>
            </HStack>
          </Box>
        )}
      </VStack>
      {UnsavedChangesDialog && <UnsavedChangesDialog blocker={blocker} />}
    </Box>
  )

  return Wrapper ? <Wrapper>{content}</Wrapper> : content
}
